/**
 * Auth hook — supports three modes:
 *  1. VITE_MOCK_AUTH=true  → instant mock login (no credentials)
 *  2. Default (no OIDC configured) → username/password via Django session
 *  3. VITE_OIDC_AUTHORITY set → redirect to Aptean IAM (Keycloak)
 *
 * SSO mode: loginWithSSO() navigates to the backend SSO initiation URL.
 * On first load after SSO redirect, restoreSession() calls /auth/me/ to
 * rehydrate auth state without requiring a re-login.
 */

import { useState, useCallback, useEffect } from "react";
import { apiUrl } from "../lib/config";

export interface AuthUser {
  email: string;
  name: string;
  username?: string;
  is_superuser?: boolean;
}

interface AuthState {
  user: AuthUser | null;
  isAuthenticated: boolean;
}

const MOCK_AUTH = import.meta.env.VITE_MOCK_AUTH === "true";
const OIDC_AUTHORITY = import.meta.env.VITE_OIDC_AUTHORITY ?? "";
const OIDC_CLIENT_ID = import.meta.env.VITE_OIDC_CLIENT_ID ?? "plteng-pulseboard";
const USE_SESSION_AUTH = !MOCK_AUTH && !OIDC_AUTHORITY;

function getStoredAuth(): AuthState {
  if (MOCK_AUTH) {
    return { user: { email: "dev@aptean.com", name: "Dev User" }, isAuthenticated: true };
  }
  const raw = sessionStorage.getItem("aptean_user");
  if (raw) {
    try {
      return { user: JSON.parse(raw), isAuthenticated: true };
    } catch { /* ignore */ }
  }
  return { user: null, isAuthenticated: false };
}

export function useAuth() {
  const [auth, setAuth] = useState<AuthState>(getStoredAuth);
  const [loginError, setLoginError] = useState<string | null>(null);
  const [loginLoading, setLoginLoading] = useState(false);

  // Restore session on mount: if no cached user, call /auth/me/ to pick up
  // a Django session that was set by the SSO callback redirect.
  useEffect(() => {
    if (MOCK_AUTH || auth.isAuthenticated) return;
    fetch(apiUrl("/api/v1/admin/auth/me/"), { credentials: "include" })
      .then((res) => (res.ok ? res.json() : null))
      .then((user) => {
        if (user) {
          sessionStorage.setItem("aptean_user", JSON.stringify(user));
          setAuth({ user, isAuthenticated: true });
        }
      })
      .catch(() => {});
  // Run only on initial mount
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loginWithCredentials = useCallback(async (username: string, password: string) => {
    setLoginLoading(true);
    setLoginError(null);
    try {
      const res = await fetch(apiUrl("/api/v1/admin/auth/login/"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ username, password }),
      });
      if (res.ok) {
        const user: AuthUser = await res.json();
        sessionStorage.setItem("aptean_user", JSON.stringify(user));
        setAuth({ user, isAuthenticated: true });
        return true;
      } else {
        const data = await res.json().catch(() => ({}));
        setLoginError(data.detail ?? "Invalid username or password.");
        return false;
      }
    } catch {
      setLoginError("Network error — is the backend running?");
      return false;
    } finally {
      setLoginLoading(false);
    }
  }, []);

  const loginWithSSO = useCallback(() => {
    window.location.href = apiUrl("/api/v1/admin/auth/sso/login/");
  }, []);

  const login = useCallback(() => {
    if (MOCK_AUTH) {
      const user = { email: "dev@aptean.com", name: "Dev User" };
      sessionStorage.setItem("aptean_user", JSON.stringify(user));
      setAuth({ user, isAuthenticated: true });
      return;
    }
    if (USE_SESSION_AUTH) return; // form-based login handled by LoginPage
    const params = new URLSearchParams({
      client_id: OIDC_CLIENT_ID,
      redirect_uri: `${window.location.origin}/admin/callback`,
      response_type: "code",
      scope: "openid email profile",
    });
    window.location.href = `${OIDC_AUTHORITY}/protocol/openid-connect/auth?${params}`;
  }, []);

  const logout = useCallback(async () => {
    sessionStorage.removeItem("aptean_user");
    setAuth({ user: null, isAuthenticated: false });
    if (USE_SESSION_AUTH || MOCK_AUTH) {
      await fetch(apiUrl("/api/v1/admin/auth/logout/"), { method: "POST", credentials: "include" }).catch(() => {});
    }
  }, []);

  return {
    ...auth,
    login,
    logout,
    loginWithCredentials,
    loginWithSSO,
    loginError,
    loginLoading,
    useSessionAuth: USE_SESSION_AUTH || MOCK_AUTH,
  };
}
